import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Card } from "@mui/material";
import Grid from "@mui/material/Grid2";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import { Add as AddIcon } from "@mui/icons-material";

// Componentes
import DocumentoAnexo from "./documentoAnexo";
import NuevoDocAnexoModal from "./nuevoDocAnexoModal";

// API
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";

const DocumentosAnexos = ({ procesoId, identificador, setError }) => {
  const [docsAnexos, setDocsAnexos] = useState([]);
  const [showNuevoDocAnexo, setShowNuevoDocAnexo] = useState(false);
  const [loading, setLoading] = useState(true);

  const cargarDocsAnexos = async () => {
    try {
      const response = await ProcesosApi.verificarAnexoProceso(procesoId, {
        tipo_anexo: "DOC_ANEXO_",
        contains: true,
      });
      if (response.data && response.data.exists) {
        const docsAnexosEncontrados = response.data.anexos.map((anexo) => ({
          id: anexo.anexo_id,
          tipo: anexo.tipo_anexo.replace("DOC_ANEXO_", ""),
          nombre_anexo: anexo.nombre_anexo,
          key_s3: anexo.key_s3,
          fecha_creacion: anexo.fecha_creacion,
          isNew: false,
        }));
        setDocsAnexos((prev) => {
          const nuevosDocsAnexos = prev.filter((e) => e.isNew);
          return [...docsAnexosEncontrados, ...nuevosDocsAnexos];
        });
      }
    } catch (error) {
      console.error("Error cargando los Documentos Anexos:", error);
      setError(obtenerMensajeError(error, "Error al cargar los Documentos Anexos"));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await cargarDocsAnexos();
      } catch (error) {
        console.error("Error cargando datos:", error);
        setError(obtenerMensajeError(error, "Error al cargar los Documentos Anexos"));
      } finally {
        setLoading(false);
      }
    };
    if (procesoId) {
      fetchData();
    }
  }, [procesoId]);

  const handleAddDocAnexo = async (tipo) => {
    try {
      const nombreNormalizadoUPPER = tipo
        .normalize("NFD") // Descompone letras acentuadas
        .replace(/[\u0300-\u036f]/g, "") // Elimina los signos diacríticos
        .replace(/ñ/g, "n") // Reemplaza ñ
        .replace(/Ñ/g, "N") // Reemplaza Ñ
        .replace(/\s+/g, "_") // Reemplaza espacios por guiones bajos
        .toUpperCase();
      // Verificar si ya existe
      if (docsAnexos.some((e) => e.tipo === nombreNormalizadoUPPER)) {
        setError(`Ya existe un Documento Anexo para el tipo ${tipo}`);
        return;
      }
      const nuevoDocAnexo = {
        id: Date.now(),
        tipo: nombreNormalizadoUPPER,
        nombre_anexo: tipo,
        isNew: true,
      };
      setDocsAnexos((prev) => [...prev, nuevoDocAnexo]);
      setShowNuevoDocAnexo(false);
    } catch (error) {
      console.error("Error agregando el Documento Anexo:", error);
      setError(obtenerMensajeError(error, "Error al agregar el Documento Anexo"));
    }
  };

  const handleDeleteDocAnexo = async (docAnexoId) => {
    try {
      const docAnexo = docsAnexos.find((e) => e.id === docAnexoId);
      if (!docAnexo) return;
      const nombreNormalizadoUPPER = docAnexo.tipo
        .normalize("NFD") // Descompone letras acentuadas
        .replace(/[\u0300-\u036f]/g, "") // Elimina los signos diacríticos
        .replace(/ñ/g, "n") // Reemplaza ñ
        .replace(/Ñ/g, "N") // Reemplaza Ñ
        .replace(/\s+/g, "_") // Reemplaza espacios por guiones bajos
        .toUpperCase();
      if (!docAnexo.isNew) {
        // Solo intentar eliminar del backend si no es nuevo
        await ProcesosApi.verificarAnexoProceso(procesoId, {
          tipo_anexo: `DOC_ANEXO_${nombreNormalizadoUPPER}`,
        });
      }
      setDocsAnexos((prev) => prev.filter((e) => e.id !== docAnexoId));
      await BucketApi.deleteSolicitudFile(nombreNormalizadoUPPER, identificador);
      await ProcesosApi.eliminarAnexoProceso(procesoId, docAnexo.id);
      await cargarDocsAnexos();
    } catch (error) {
      console.error("Error eliminando el Documento Anexo:", error);
      setError(obtenerMensajeError(error, "Error al eliminar el Documento Anexo"));
    }
  };

  if (loading) {
    return (
      <SuiBox display="flex" justifyContent="center" p={3}>
        <SuiTypography variant="button" color="text" fontWeight="regular">
          Cargando Documentos Anexos...
        </SuiTypography>
      </SuiBox>
    );
  }

  return (
    <Card>
      <SuiBox p={2}>
        <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SuiTypography variant="caption" color="text">
            Documentos que aportan informacion adicional a la Solicitud
          </SuiTypography>
          <SuiButton
            variant="gradient"
            buttonColor="info"
            onClick={() => setShowNuevoDocAnexo(true)}
            icon={<AddIcon />}
          >
            Agregar
          </SuiButton>
        </SuiBox>
        <Grid container spacing={3}>
          {docsAnexos.map((docAnexo) => (
            <Grid item xs={12} md={6} key={docAnexo.id || `temp-${docAnexo.tipo}`}>
              <DocumentoAnexo
                docAnexo={docAnexo}
                procesoId={procesoId}
                identificador={identificador}
                onDelete={handleDeleteDocAnexo}
                setError={setError}
              />
            </Grid>
          ))}
        </Grid>
        <NuevoDocAnexoModal
          open={showNuevoDocAnexo}
          onClose={() => setShowNuevoDocAnexo(false)}
          onSubmit={handleAddDocAnexo}
          docsExistentes={docsAnexos}
        />
      </SuiBox>
    </Card>
  );
};

DocumentosAnexos.propTypes = {
  procesoId: PropTypes.number.isRequired,
  identificador: PropTypes.string,
  setError: PropTypes.func.isRequired,
};

export default DocumentosAnexos;
